import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["keyField", "secretField"];
  static values = {
    visibleChars: { type: Number, default: 8 },
    maxAttempts: { type: Number, default: 3 },
  };

  connect() {
    this.attempts = 0;
    this.initializeCredentials();
  }

  initializeCredentials() {
    this.credentialFields.forEach((field) => {
      const initialValue = field.dataset.initialValue;
      if (!initialValue) {
        const formattedValue = field.dataset.formattedValue;
        if (formattedValue) field.value = formattedValue;
        return;
      }
      this.securelyStoreCredential(field, initialValue);
      field.value = this.formatValue(initialValue);
      this.cleanupInitialValue(field);
    });
  }

  get credentialFields() {
    return [this.keyFieldTarget, this.secretFieldTarget];
  }

  async copyKey(event) {
    await this.securelyCopyCredential("key", event.currentTarget);
  }

  async copySecret(event) {
    await this.securelyCopyCredential("secret", event.currentTarget);
  }

  async securelyCopyCredential(type, button) {
    if (this.attempts >= this.maxAttemptsValue) {
      this.notifyError("Too many attempts. Please refresh the page.");
      return;
    }

    try {
      const field = this[`${type}FieldTarget`];
      const credential = this.getSecureCredential(field);
      if (!credential) throw new Error(`No ${type} credential available`);

      await this.copyToClipboard(credential);
      const formattedValue = await this.markCredentialCopied(type);

      field.value = formattedValue;
      this.cleanupSecureValue(field);
      button.classList.add("hidden");

      this.notifySuccess();
    } catch (error) {
      console.error(`Copy failed: ${error.message}`);
      this.attempts++;
      this.notifyError();
    }
  }

  securelyStoreCredential(field, value) {
    try {
      const encoder = new TextEncoder();
      const encoded = encoder.encode(value);
      field.dataset.secureValue = btoa(String.fromCharCode(...encoded));
    } catch (error) {
      console.error("Failed to store credential securely");
      this.cleanupSecureValue(field);
    }
  }

  getSecureCredential(field) {
    if (!field.dataset.secureValue) return null;
    try {
      const decoder = new TextDecoder();
      const decoded = Uint8Array.from(atob(field.dataset.secureValue), (c) =>
        c.charCodeAt(0)
      );
      return decoder.decode(decoded);
    } catch (error) {
      console.error("Failed to retrieve credential");
      this.cleanupSecureValue(field);
      return null;
    }
  }

  formatValue(value) {
    if (!value) return "";
    const chars = this.visibleCharsValue;
    return `${value.slice(0, chars)}${"*".repeat(
      Math.max(0, value.length - chars * 2)
    )}${value.slice(-chars)}`;
  }

  async markCredentialCopied(type) {
    const response = await fetch(
      `${window.location.pathname}/mark_credential_copied`,
      {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ type }),
      }
    );

    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    const data = await response.json();
    if (!data.formatted_value) throw new Error("Invalid server response");
    return data.formatted_value;
  }

  async copyToClipboard(text) {
    if (!text) throw new Error("No text to copy");
    await navigator.clipboard.writeText(text);
  }

  notifySuccess() {
    this.dispatch("toast", {
      detail: { message: "Credentials copied to clipboard", type: "success" },
    });
  }

  notifyError(message = "Failed to copy credentials") {
    this.dispatch("toast", {
      detail: { message, type: "error" },
    });
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  cleanupInitialValue(field) {
    delete field.dataset.initialValue;
    field.removeAttribute("data-initial-value");
  }

  cleanupSecureValue(field) {
    delete field.dataset.secureValue;
    field.removeAttribute("data-secure-value");
  }
}
